import { defineMessages, type MessageDescriptor } from 'react-intl';

type GraphClawHubMessages = Record<string, MessageDescriptor>;

export const messages: GraphClawHubMessages = defineMessages({
	graphClawControl: {
		id: 'dtac.graphClawHub.graphClawControl',
		defaultMessage: 'GraphClaw',
		description: 'Name of the local GraphClaw Hub preview control.',
	},
	closeGraphClawControl: {
		id: 'dtac.graphClawHub.closeGraphClawControl',
		defaultMessage: 'Close GraphClaw',
		description: 'Accessible label for closing the local GraphClaw Hub preview control.',
	},
	mockV2: {
		id: 'dtac.graphClawHub.mockV2',
		defaultMessage: 'Mock',
		description: 'Label for previewing the V2 GraphClaw Hub with in-memory data.',
	},
	mockJsmFlow: {
		id: 'dtac.graphClawHub.mockJsmFlow.ai-non-final',
		defaultMessage: 'Mock JSM flow',
		description:
			'Label for adding a sample JSM incident report app, its reports, and Tune App to the mock preview.',
	},
	navigationLabel: {
		id: 'dtac.graphClawHub.navigationLabel',
		defaultMessage: 'Graph Claw views',
		description: 'Accessible label for the GraphClaw Hub local navigation.',
	},
	navDashboard: {
		id: 'dtac.graphClawHub.navDashboard',
		defaultMessage: 'Dashboard',
		description: 'Navigation item for the GraphClaw Hub dashboard view.',
	},
	navMyApps: {
		id: 'dtac.graphClawHub.navMyApps',
		defaultMessage: 'Apps',
		description: 'Navigation item for the GraphClaw Hub apps view.',
	},
	navInferences: {
		id: 'dtac.graphClawHub.navInferences',
		defaultMessage: 'Explorer',
		description: 'Navigation item for exploring accessible inference data.',
	},
	navReview: {
		id: 'dtac.graphClawHub.navReview',
		defaultMessage: 'Review',
		description: 'Navigation item for labeling and judging inference data.',
	},
	navMyInference: {
		id: 'dtac.graphClawHub.navMyInference',
		defaultMessage: 'My Inference',
		description: 'Navigation item for the selected inference app view.',
	},
	evalResultsReady: {
		id: 'dtac.graphClawHub.evalResultsReady',
		defaultMessage: 'Your eval just finished',
		description: 'Notification shown when Eval requests finish.',
	},
	viewEvals: {
		id: 'dtac.graphClawHub.viewEvals',
		defaultMessage: 'here',
		description: 'Visible button text that opens the completed Eval.',
	},
	viewCompletedEval: {
		id: 'dtac.graphClawHub.viewCompletedEval',
		defaultMessage: 'View completed eval',
		description: 'Accessible label for the button that opens the completed Eval.',
	},
	navRegistry: {
		id: 'dtac.graphClawHub.navRegistry',
		defaultMessage: 'Registry',
		description: 'Disabled navigation item for the future registry view.',
	},
	navTryIt: {
		id: 'dtac.graphClawHub.navTryIt',
		defaultMessage: 'Try it',
		description: 'Disabled navigation item for the future try-it view.',
	},
	navAuditLog: {
		id: 'dtac.graphClawHub.navAuditLog',
		defaultMessage: 'Audit log',
		description: 'Disabled navigation item for the future audit log view.',
	},
	dashboardTitle: {
		id: 'dtac.graphClawHub.dashboardTitle',
		defaultMessage: 'Dashboard',
		description: 'Heading for the GraphClaw Hub dashboard view.',
	},
	dashboardDescription: {
		id: 'dtac.graphClawHub.dashboardDescription',
		defaultMessage: 'Platform overview and health metrics.',
		description: 'Sub-heading for the GraphClaw Hub dashboard view.',
	},
	registryOverviewTitle: {
		id: 'dtac.graphClawHub.registryOverviewTitle',
		defaultMessage: 'Registry overview',
		description: 'Heading for the registry overview table on the dashboard.',
	},
	registryOverviewViewAll: {
		id: 'dtac.graphClawHub.registryOverviewViewAll',
		defaultMessage: 'View all',
		description: 'Link text for viewing all registry entries.',
	},
	statusBreakdownTitle: {
		id: 'dtac.graphClawHub.statusBreakdownTitle',
		defaultMessage: 'Status breakdown',
		description: 'Heading for the status breakdown chart card.',
	},
	topTeamsTitle: {
		id: 'dtac.graphClawHub.topTeamsTitle',
		defaultMessage: 'Top teams',
		description: 'Heading for the top teams chart card.',
	},
	metricActiveInferencesLabel: {
		id: 'dtac.graphClawHub.metricActiveInferencesLabel',
		defaultMessage: 'Active inferences',
		description: 'Metric card label for active inference count.',
	},
	metricEntitiesGeneratedLabel: {
		id: 'dtac.graphClawHub.metricEntitiesGeneratedLabel',
		defaultMessage: 'Entities generated',
		description: 'Metric card label for generated entity count.',
	},
	metricEntitiesGeneratedDetail: {
		id: 'dtac.graphClawHub.metricEntitiesGeneratedDetail',
		defaultMessage: 'this week',
		description: 'Supporting label for the generated entity metric timeframe.',
	},
	metricConsumersLabel: {
		id: 'dtac.graphClawHub.metricConsumersLabel',
		defaultMessage: 'Consumers',
		description: 'Metric card label for consumers count.',
	},
	metricConsumersDetail: {
		id: 'dtac.graphClawHub.metricConsumersDetail',
		defaultMessage: 'Graph is the #1 destination',
		description: 'Supporting label for the consumers metric.',
	},
	metricAvgConfidenceLabel: {
		id: 'dtac.graphClawHub.metricAvgConfidenceLabel',
		defaultMessage: 'Avg confidence',
		description: 'Metric card label for average confidence.',
	},
	metricMauLabel: {
		id: 'dtac.graphClawHub.metricMauLabel',
		defaultMessage: 'MAU / inferred object',
		description: 'Metric card label for monthly active users per inferred object.',
	},
	metricMauDetail: {
		id: 'dtac.graphClawHub.metricMauDetail',
		defaultMessage: 'avg monthly active users',
		description: 'Supporting label for the monthly active user metric.',
	},
	metricGraphTraversalsLabel: {
		id: 'dtac.graphClawHub.metricGraphTraversalsLabel',
		defaultMessage: 'Graph traversals',
		description: 'Metric card label for graph traversal count.',
	},
	metricGraphTraversalsDetail: {
		id: 'dtac.graphClawHub.metricGraphTraversalsDetail',
		defaultMessage: 'last 30 days',
		description: 'Supporting label for the graph traversal timeframe.',
	},
	metricHealthyInferencesLabel: {
		id: 'dtac.graphClawHub.metricHealthyInferencesLabel',
		defaultMessage: 'Healthy inferences',
		description: 'Metric card label for healthy inference percentage.',
	},
	metricHealthyInferencesDetail: {
		id: 'dtac.graphClawHub.metricHealthyInferencesDetail',
		defaultMessage: '{green} green - {yellow} yellow - {red} red',
		description: 'Supporting label listing inference health counts by status.',
	},
	metricNeedsAttentionLabel: {
		id: 'dtac.graphClawHub.metricNeedsAttentionLabel',
		defaultMessage: 'Needs attention',
		description: 'Metric card label for inferences needing attention.',
	},
	metricNeedsAttentionDetail: {
		id: 'dtac.graphClawHub.metricNeedsAttentionDetail',
		defaultMessage: 'yellow or red health',
		description: 'Supporting label explaining which health states need attention.',
	},
	demoLozenge: {
		id: 'dtac.graphClawHub.demoLozenge',
		defaultMessage: 'Demo',
		description: 'Lozenge text identifying demo-only dashboard metrics.',
	},
	tableNameHeader: {
		id: 'dtac.graphClawHub.tableNameHeader',
		defaultMessage: 'Name',
		description: 'Registry overview table header for inference name.',
	},
	tableTeamHeader: {
		id: 'dtac.graphClawHub.tableTeamHeader',
		defaultMessage: 'Team',
		description: 'Registry overview table header for owning team.',
	},
	tableStatusHeader: {
		id: 'dtac.graphClawHub.tableStatusHeader',
		defaultMessage: 'Status',
		description: 'Registry overview table header for inference status.',
	},
	tableHealthHeader: {
		id: 'dtac.graphClawHub.tableHealthHeader',
		defaultMessage: 'Health',
		description: 'Registry overview table header for inference health.',
	},
	tableOutputEntityHeader: {
		id: 'dtac.graphClawHub.tableOutputEntityHeader',
		defaultMessage: 'Output entity',
		description: 'Registry overview table header for output entity type.',
	},
	tableNextRunHeader: {
		id: 'dtac.graphClawHub.tableNextRunHeader',
		defaultMessage: 'Next run',
		description: 'Registry overview table header for next run date and time.',
	},
	tableLabel: {
		id: 'dtac.graphClawHub.tableLabel',
		defaultMessage: 'Registry overview',
		description: 'Accessible label for the registry overview table.',
	},
	statusActive: {
		id: 'dtac.graphClawHub.statusActive',
		defaultMessage: 'Active',
		description: 'Status label for active inferences.',
	},
	statusPendingReview: {
		id: 'dtac.graphClawHub.statusPendingReview',
		defaultMessage: 'Pending review',
		description: 'Status label for inferences pending review.',
	},
	statusRetired: {
		id: 'dtac.graphClawHub.statusRetired',
		defaultMessage: 'Retired',
		description: 'Status label for retired inferences.',
	},
	healthGreen: {
		id: 'dtac.graphClawHub.healthGreen',
		defaultMessage: 'Green',
		description: 'Health label for healthy green state.',
	},
	healthYellow: {
		id: 'dtac.graphClawHub.healthYellow',
		defaultMessage: 'Yellow',
		description: 'Health label for warning yellow state.',
	},
	healthRed: {
		id: 'dtac.graphClawHub.healthRed',
		defaultMessage: 'Red',
		description: 'Health label for critical red state.',
	},
	nextRunUnavailable: {
		id: 'dtac.graphClawHub.nextRunUnavailable',
		defaultMessage: 'Not scheduled',
		description: 'Accessible text for a registry row that has no next run scheduled.',
	},
	chartDatumLabel: {
		id: 'dtac.graphClawHub.chartDatumLabel',
		defaultMessage: '{label}: {value}',
		description: 'Accessible label for one row in a dashboard bar chart.',
	},
	statusDraft: {
		id: 'dtac.graphClawHub.statusDraft.ai-non-final',
		defaultMessage: 'Draft',
		description: 'Status label for draft inferences.',
	},
	statusStale: {
		id: 'dtac.graphClawHub.statusStale.ai-non-final',
		defaultMessage: 'Stale',
		description: 'Status label for stale inferences.',
	},
	registryTitle: {
		id: 'dtac.graphClawHub.registryTitle.ai-non-final',
		defaultMessage: 'Registry',
		description: 'Heading for the GraphClaw Hub registry view.',
	},
	registryDescription: {
		id: 'dtac.graphClawHub.registryDescription.ai-non-final',
		defaultMessage: 'Reusable building blocks for inference apps.',
		description: 'Sub-heading for the GraphClaw Hub registry view.',
	},
	registrySearchPlaceholder: {
		id: 'dtac.graphClawHub.registrySearchPlaceholder.ai-non-final',
		defaultMessage: 'Search the registry',
		description: 'Placeholder for the registry search field.',
	},
	registrySearchLabel: {
		id: 'dtac.graphClawHub.registrySearchLabel.ai-non-final',
		defaultMessage: 'Search the registry',
		description: 'Accessible label for the registry search field.',
	},
	registryAll: {
		id: 'dtac.graphClawHub.registryAll.ai-non-final',
		defaultMessage: 'All',
		description: 'Registry tab that shows every category.',
	},
	registrySources: {
		id: 'dtac.graphClawHub.registrySources.ai-non-final',
		defaultMessage: 'Sources',
		description: 'Registry category for source products.',
	},
	registryInferenceTypes: {
		id: 'dtac.graphClawHub.registryInferenceTypes.ai-non-final',
		defaultMessage: 'Inference types',
		description: 'Registry category for inference types.',
	},
	registryTargets: {
		id: 'dtac.graphClawHub.registryTargets.ai-non-final',
		defaultMessage: 'Targets',
		description: 'Registry category for target capabilities.',
	},
	registryDestinations: {
		id: 'dtac.graphClawHub.registryDestinations.ai-non-final',
		defaultMessage: 'Destinations',
		description: 'Registry category for output destinations.',
	},
	registryTeamworkGraphDestination: {
		id: 'dtac.graphClawHub.registryTeamworkGraphDestination.ai-non-final',
		defaultMessage: 'Teamwork Graph',
		description:
			'Name shown for the Teamwork Graph destination in registry cards and registry details.',
	},
	registryShadowDestination: {
		id: 'dtac.graphClawHub.registryShadowDestination.ai-non-final',
		defaultMessage: 'Shadow only',
		description: 'Name shown for the shadow destination in registry cards and registry details.',
	},
	registryAvailable: {
		id: 'dtac.graphClawHub.registryAvailable.ai-non-final',
		defaultMessage: 'Available',
		description: 'Status for a registry item that can be used.',
	},
	registryUnavailable: {
		id: 'dtac.graphClawHub.registryUnavailable.ai-non-final',
		defaultMessage: 'Unavailable',
		description: 'Status for a registry item that cannot be used.',
	},
	registryUsedBy: {
		id: 'dtac.graphClawHub.registryUsedBy.ai-non-final',
		defaultMessage: '{count, plural, one {Used by # app} other {Used by # apps}}',
		description: 'Number of inference apps using a registry item.',
	},
	registryViewDetails: {
		id: 'dtac.graphClawHub.registryViewDetails.ai-non-final',
		defaultMessage: 'View details',
		description: 'Text on a registry card that opens its details.',
	},
	registryDetails: {
		id: 'dtac.graphClawHub.registryDetails.ai-non-final',
		defaultMessage: 'Registry details',
		description: 'Heading for the selected registry item details.',
	},
	registryType: {
		id: 'dtac.graphClawHub.registryType.ai-non-final',
		defaultMessage: 'Type',
		description: 'Label for the category of a registry item.',
	},
	registryAvailability: {
		id: 'dtac.graphClawHub.registryAvailability.ai-non-final',
		defaultMessage: 'Availability',
		description: 'Label for registry item availability.',
	},
	registryUsage: {
		id: 'dtac.graphClawHub.registryUsage.ai-non-final',
		defaultMessage: 'Usage',
		description: 'Label for registry item app usage.',
	},
	registryEvents: {
		id: 'dtac.graphClawHub.registryEvents.ai-non-final',
		defaultMessage: 'Events',
		description: 'Label for source events in registry details.',
	},
	registryResultKind: {
		id: 'dtac.graphClawHub.registryResultKind.ai-non-final',
		defaultMessage: 'Result type',
		description: 'Label for the result type in registry details.',
	},
	registrySelectItem: {
		id: 'dtac.graphClawHub.registrySelectItem.ai-non-final',
		defaultMessage: 'Select an item to view its details.',
		description: 'Empty state in the registry detail panel.',
	},
	registryNoResults: {
		id: 'dtac.graphClawHub.registryNoResults.ai-non-final',
		defaultMessage: 'No matching registry items',
		description: 'Heading shown when registry search has no matches.',
	},
	registryNoResultsDescription: {
		id: 'dtac.graphClawHub.registryNoResultsDescription.ai-non-final',
		defaultMessage: 'Try a different search term or category.',
		description: 'Guidance shown when registry search has no matches.',
	},
	registryEmpty: {
		id: 'dtac.graphClawHub.registryEmpty.ai-non-final',
		defaultMessage: 'The registry is empty',
		description: 'Heading shown when the service returns no registry options.',
	},
	registryEmptyDescription: {
		id: 'dtac.graphClawHub.registryEmptyDescription.ai-non-final',
		defaultMessage: 'No sources, inference types, targets, or destinations are available.',
		description: 'Description shown when the service returns no registry options.',
	},
	registryLoadError: {
		id: 'dtac.graphClawHub.registryLoadError.ai-non-final',
		defaultMessage: 'We could not load the registry',
		description: 'Heading shown when registry data cannot be loaded.',
	},
	registryLoadErrorDescription: {
		id: 'dtac.graphClawHub.registryLoadErrorDescription.ai-non-final',
		defaultMessage: 'Try loading the registry again.',
		description: 'Description shown when registry data cannot be loaded.',
	},
	auditLogTitle: {
		id: 'dtac.graphClawHub.auditLogTitle.ai-non-final',
		defaultMessage: 'Audit Log',
		description: 'Heading for the GraphClaw Hub audit log view.',
	},
	auditLogDescription: {
		id: 'dtac.graphClawHub.auditLogDescription.ai-non-final',
		defaultMessage: 'Lifecycle events and inferences needing attention.',
		description: 'Sub-heading for the GraphClaw Hub audit log view.',
	},
	bucketStale: {
		id: 'dtac.graphClawHub.bucketStale.ai-non-final',
		defaultMessage: 'Stale',
		description: 'Title of the audit log bucket for stale inferences.',
	},
	bucketUnused: {
		id: 'dtac.graphClawHub.bucketUnused.ai-non-final',
		defaultMessage: 'Unused',
		description: 'Title of the audit log bucket for unused inferences.',
	},
	bucketLowQuality: {
		id: 'dtac.graphClawHub.bucketLowQuality.ai-non-final',
		defaultMessage: 'Low Quality',
		description: 'Title of the audit log bucket for low quality inferences.',
	},
	bucketExpiring: {
		id: 'dtac.graphClawHub.bucketExpiring.ai-non-final',
		defaultMessage: 'Expiring',
		description: 'Title of the audit log bucket for expiring inferences.',
	},
	bucketEmpty: {
		id: 'dtac.graphClawHub.bucketEmpty.ai-non-final',
		defaultMessage: 'Nothing needs attention.',
		description: 'Empty state shown in an audit log bucket that has no inferences.',
	},
	bucketMore: {
		id: 'dtac.graphClawHub.bucketMore.ai-non-final',
		defaultMessage: '{count, number} more',
		description: 'Expander label for additional inferences in an audit log bucket.',
	},
	actionScheduleReview: {
		id: 'dtac.graphClawHub.actionScheduleReview.ai-non-final',
		defaultMessage: 'Schedule Review',
		description: 'Button to schedule a review of an inference needing attention.',
	},
	actionRetire: {
		id: 'dtac.graphClawHub.actionRetire.ai-non-final',
		defaultMessage: 'Retire',
		description: 'Button to retire an inference needing attention.',
	},
	actionContactOwner: {
		id: 'dtac.graphClawHub.actionContactOwner.ai-non-final',
		defaultMessage: 'Contact Owner',
		description: 'Button to contact the owner of an inference needing attention.',
	},
	activityTitle: {
		id: 'dtac.graphClawHub.activityTitle.ai-non-final',
		defaultMessage: 'Activity',
		description: 'Heading for the audit log activity feed section.',
	},
	activityFilterLabel: {
		id: 'dtac.graphClawHub.activityFilterLabel.ai-non-final',
		defaultMessage: 'Filter activity by action',
		description: 'Accessible label for the (decorative) audit log activity action filter.',
	},
	activityFilterAllActions: {
		id: 'dtac.graphClawHub.activityFilterAllActions.ai-non-final',
		defaultMessage: 'All actions',
		description: 'Default option of the audit log activity action filter.',
	},
	activitySearchPlaceholder: {
		id: 'dtac.graphClawHub.activitySearchPlaceholder.ai-non-final',
		defaultMessage: 'Search log...',
		description: 'Placeholder for the (decorative) audit log activity search field.',
	},
	activitySearchLabel: {
		id: 'dtac.graphClawHub.activitySearchLabel.ai-non-final',
		defaultMessage: 'Search activity log',
		description: 'Accessible label for the (decorative) audit log activity search field.',
	},
	activityActionCreated: {
		id: 'dtac.graphClawHub.activityActionCreated.ai-non-final',
		defaultMessage: 'Created',
		description: 'Lozenge label for an audit log entry where an inference was created.',
	},
	activityActionRetired: {
		id: 'dtac.graphClawHub.activityActionRetired.ai-non-final',
		defaultMessage: 'Retired',
		description: 'Lozenge label for an audit log entry where an inference was retired.',
	},
	activityCreatedDetail: {
		id: 'dtac.graphClawHub.activityCreatedDetail.ai-non-final',
		defaultMessage: 'Inference "{name}" registered under {scope}',
		description: 'Description of an audit log entry where an inference was registered.',
	},
	activityRetiredDetail: {
		id: 'dtac.graphClawHub.activityRetiredDetail.ai-non-final',
		defaultMessage: 'Inference "{name}" deleted from registry',
		description: 'Description of an audit log entry where an inference was deleted.',
	},
	tryItTitle: {
		id: 'dtac.graphClawHub.tryItTitle.ai-non-final',
		defaultMessage: 'Try It',
		description: 'Heading for the GraphClaw Hub try-it query playground view.',
	},
	tryItDescription: {
		id: 'dtac.graphClawHub.tryItDescription.ai-non-final',
		defaultMessage: 'Explore inferred entities in the Teamwork Graph.',
		description: 'Sub-heading for the GraphClaw Hub try-it view.',
	},
	entityTypeLabel: {
		id: 'dtac.graphClawHub.entityTypeLabel.ai-non-final',
		defaultMessage: 'Entity type',
		description: 'Label for the entity type select in the try-it query builder.',
	},
	sourceInferenceLabel: {
		id: 'dtac.graphClawHub.sourceInferenceLabel.ai-non-final',
		defaultMessage: 'Source inference',
		description: 'Label for the source inference select in the try-it query builder.',
	},
	minConfidenceLabel: {
		id: 'dtac.graphClawHub.minConfidenceLabel.ai-non-final',
		defaultMessage: 'Min. confidence',
		description: 'Label for the minimum confidence slider in the try-it query builder.',
	},
	scopeLabel: {
		id: 'dtac.graphClawHub.scopeLabel.ai-non-final',
		defaultMessage: 'Scope',
		description: 'Label for the scope select in the try-it query builder.',
	},
	dateRangeLabel: {
		id: 'dtac.graphClawHub.dateRangeLabel.ai-non-final',
		defaultMessage: 'Date range',
		description: 'Label for the date range select in the try-it query builder.',
	},
	runQuery: {
		id: 'dtac.graphClawHub.runQuery.ai-non-final',
		defaultMessage: 'Run Query',
		description: 'Button that runs the try-it query.',
	},
	copyApiCall: {
		id: 'dtac.graphClawHub.copyApiCall.ai-non-final',
		defaultMessage: 'Copy API Call',
		description: 'Button that copies the generated API call to the clipboard.',
	},
	tabResults: {
		id: 'dtac.graphClawHub.tabResults.ai-non-final',
		defaultMessage: 'Results',
		description: 'Tab and panel heading for the try-it query results.',
	},
	tabCodeSnippets: {
		id: 'dtac.graphClawHub.tabCodeSnippets.ai-non-final',
		defaultMessage: 'Code Snippets',
		description: 'Tab label for the try-it code snippets.',
	},
	resultsEmpty: {
		id: 'dtac.graphClawHub.resultsEmpty.ai-non-final',
		defaultMessage: 'Run a query to see inferred entities.',
		description: 'Empty state shown in the try-it results panel before a query is run.',
	},
	notEnabledTitle: {
		id: 'dtac.graphClawHub.notEnabledTitle.ai-non-final',
		defaultMessage: 'Try It isn’t enabled yet',
		description: 'Title of the flag shown when the user runs a query in the try-it view.',
	},
	notEnabledDescription: {
		id: 'dtac.graphClawHub.notEnabledDescription.ai-non-final',
		defaultMessage: 'This feature isn’t enabled yet. Your query wasn’t run.',
		description: 'Body of the flag shown when the user runs a query in the try-it view.',
	},
	dismissBanner: {
		id: 'dtac.graphClawHub.dismissBanner.ai-non-final',
		defaultMessage: 'Dismiss',
		description: 'Button that dismisses the try-it "feature not enabled" flag.',
	},
	pageTitle: {
		id: 'dtac.graphClawHub.pageTitle',
		defaultMessage: 'Apps',
		description: 'Heading for the GraphClaw Hub page which lists a user’s inference apps.',
	},
	pageDescription: {
		id: 'dtac.graphClawHub.pageDescription',
		defaultMessage: 'Compose apps that infer insights from your tools across the Teamwork Graph.',
		description: 'Sub-heading describing what inference apps do on the GraphClaw Hub page.',
	},
	graphClawAppsHeading: {
		id: 'dtac.graphClawHub.graphClawAppsHeading.ai-non-final',
		defaultMessage: 'GraphClaw Apps',
		description: 'Heading above inference apps created and operated by GraphClaw.',
	},
	externallyManagedAppsHeading: {
		id: 'dtac.graphClawHub.externallyManagedAppsHeading.ai-non-final',
		defaultMessage: 'Externally managed apps',
		description: 'Heading above inference apps operated outside GraphClaw.',
	},
	externallyManagedAppsDescription: {
		id: 'dtac.graphClawHub.externallyManagedAppsDescription.ai-non-final',
		defaultMessage: 'Inference apps operated outside GraphClaw. They are view-only here.',
		description: 'Explanation below the externally managed apps heading.',
	},
	externallyManagedLozenge: {
		id: 'dtac.graphClawHub.externallyManagedLozenge.ai-non-final',
		defaultMessage: 'Externally managed',
		description: 'Status lozenge identifying an inference app operated outside GraphClaw.',
	},
	addButton: {
		id: 'dtac.graphClawHub.addButton',
		defaultMessage: 'Add app',
		description: 'Label for the button that opens the modal to create a new inference app.',
	},
	addButtonAriaLabel: {
		id: 'dtac.graphClawHub.addButtonAriaLabel',
		defaultMessage: 'Add inference app',
		description: 'Accessible label for the Add app button on the GraphClaw Hub page.',
	},
	modalTitle: {
		id: 'dtac.graphClawHub.modalTitle',
		defaultMessage: 'Add inference app',
		description: 'Title of the modal used to create a new inference app.',
	},
	editModalTitle: {
		id: 'dtac.graphClawHub.editModalTitle.ai-non-final',
		defaultMessage: 'Edit inference app',
		description: 'Title of the modal used to edit an existing inference app configuration.',
	},
	modalSubtitle: {
		id: 'dtac.graphClawHub.modalSubtitle',
		defaultMessage: 'Choose product, target, and inference type.',
		description: 'Sub-heading under the inference app modal title explaining the setup fields.',
	},
	nameFieldLabel: {
		id: 'dtac.graphClawHub.nameFieldLabel',
		defaultMessage: 'Name',
		description: 'Label for the optional name field in the inference app modal.',
	},
	nameFieldPlaceholder: {
		id: 'dtac.graphClawHub.nameFieldPlaceholder',
		defaultMessage: 'Generated from selection if blank',
		description: 'Placeholder for the optional name field in the inference app modal.',
	},
	generatedAppName: {
		id: 'dtac.graphClawHub.generatedAppName.ai-non-final',
		defaultMessage: 'Infer {inferenceType} for {target} with {product}',
		description:
			'Default inference app name generated from the selected inference type, target, and product.',
	},
	productFieldLabel: {
		id: 'dtac.graphClawHub.productFieldLabel.ai-non-final',
		defaultMessage: 'Product',
		description: 'Label for the product select in the inference app modal.',
	},
	productFieldPlaceholder: {
		id: 'dtac.graphClawHub.productFieldPlaceholder.ai-non-final',
		defaultMessage: 'Select a product',
		description: 'Placeholder for the product select before a product option is chosen.',
	},
	productFieldHelp: {
		id: 'dtac.graphClawHub.productFieldHelp.ai-non-final',
		defaultMessage: 'Only enabled products can be selected.',
		description: 'Helper text under the product select explaining unavailable options.',
	},
	withFieldLabel: {
		id: 'dtac.graphClawHub.withFieldLabel',
		defaultMessage: 'With',
		description: 'Label for the product select in the inference app modal.',
	},
	withFieldPlaceholder: {
		id: 'dtac.graphClawHub.withFieldPlaceholder',
		defaultMessage: 'Select a product',
		description: 'Placeholder for the product select before a product option is chosen.',
	},
	withFieldHelp: {
		id: 'dtac.graphClawHub.withFieldHelp',
		defaultMessage: 'The product events to listen to.',
		description: 'Helper text under the product select explaining available options.',
	},
	matcherFieldLabel: {
		id: 'dtac.graphClawHub.matcherFieldLabel.ai-non-final',
		defaultMessage: 'When',
		description: 'Label for the matcher multi-select in the inference app modal.',
	},
	matcherFieldPlaceholder: {
		id: 'dtac.graphClawHub.matcherFieldPlaceholder.ai-non-final',
		defaultMessage: 'Select at least one event',
		description: 'Placeholder for the required matcher multi-select in the inference app modal.',
	},
	matcherFieldHelp: {
		id: 'dtac.graphClawHub.matcherFieldHelp.ai-non-final',
		defaultMessage: 'The source events that start this inference.',
		description: 'Helper text below the matcher multi-select in the inference app modal.',
	},
	targetFieldLabel: {
		id: 'dtac.graphClawHub.targetFieldLabel.ai-non-final',
		defaultMessage: 'Target',
		description: 'Label for the inference target select in the inference app modal.',
	},
	targetFieldPlaceholder: {
		id: 'dtac.graphClawHub.targetFieldPlaceholder.ai-non-final',
		defaultMessage: 'Select a target',
		description: 'Placeholder for the target select before a target option is chosen.',
	},
	targetFieldHelp: {
		id: 'dtac.graphClawHub.targetFieldHelp.ai-non-final',
		defaultMessage: 'Only enabled targets can be selected.',
		description: 'Helper text under the target select explaining unavailable options.',
	},
	forFieldLabel: {
		id: 'dtac.graphClawHub.forFieldLabel',
		defaultMessage: 'For',
		description: 'Label for the inference target select in the inference app modal.',
	},
	forFieldPlaceholder: {
		id: 'dtac.graphClawHub.forFieldPlaceholder',
		defaultMessage: 'Select a target',
		description: 'Placeholder for the inference target select before a target option is chosen.',
	},
	forFieldHelp: {
		id: 'dtac.graphClawHub.forFieldHelp',
		defaultMessage: 'The target to scope inference to.',
		description: 'Helper text under the inference target select.',
	},
	typeFieldLabel: {
		id: 'dtac.graphClawHub.typeFieldLabel.ai-non-final',
		defaultMessage: 'Type',
		description: 'Label for the inference type select in the inference app modal.',
	},
	typeFieldPlaceholder: {
		id: 'dtac.graphClawHub.typeFieldPlaceholder.ai-non-final',
		defaultMessage: 'Select a type',
		description: 'Placeholder for the type select before an inference type is chosen.',
	},
	typeFieldHelp: {
		id: 'dtac.graphClawHub.typeFieldHelp.ai-non-final',
		defaultMessage: 'Only enabled inference types can be selected.',
		description: 'Helper text under the type select explaining unavailable options.',
	},
	inferFieldLabel: {
		id: 'dtac.graphClawHub.inferFieldLabel',
		defaultMessage: 'Infer',
		description: 'Label for the inference type select in the inference app modal.',
	},
	inferFieldPlaceholder: {
		id: 'dtac.graphClawHub.inferFieldPlaceholder',
		defaultMessage: 'Select what to infer',
		description: 'Placeholder for the inference type select before an inference type is chosen.',
	},
	inferFieldHelp: {
		id: 'dtac.graphClawHub.inferFieldHelp',
		defaultMessage: 'What to infer from the source.',
		description: 'Helper text under the inference type select.',
	},
	promptOverrideFieldLabel: {
		id: 'dtac.graphClawHub.promptOverrideFieldLabel.ai-non-final',
		defaultMessage: 'Additional instructions',
		description: 'Label for the optional literal instruction text area in the inference app modal.',
	},
	promptOverrideFieldPlaceholder: {
		id: 'dtac.graphClawHub.promptOverrideFieldPlaceholder.ai-non-final',
		defaultMessage: 'For example, prioritize launch decisions',
		description: 'Example placeholder in the optional literal instruction text area.',
	},
	promptOverrideFieldHelp: {
		id: 'dtac.graphClawHub.promptOverrideFieldHelp.ai-non-final',
		defaultMessage:
			'Optional guidance for what this app should infer. Access, safety, and output rules still apply.',
		description:
			'Helper text explaining the limited purpose of optional inference app instructions.',
	},
	promptOverrideToggleLabel: {
		id: 'dtac.graphClawHub.promptOverrideToggleLabel.ai-non-final',
		defaultMessage: 'Add additional instructions',
		description: 'Label for showing or hiding the optional inference app instructions field.',
	},
	promptOverrideToggleDescription: {
		id: 'dtac.graphClawHub.promptOverrideToggleDescription.ai-non-final',
		defaultMessage: 'Give this app extra guidance for what to look for.',
		description: 'Helper text next to the optional inference app instructions toggle.',
	},
	enabledFieldLabel: {
		id: 'dtac.graphClawHub.enabledFieldLabel.ai-non-final',
		defaultMessage: 'Enabled',
		description: 'Label for the toggle that controls whether an inference app can run.',
	},
	enabledFieldDescription: {
		id: 'dtac.graphClawHub.enabledFieldDescription.ai-non-final',
		defaultMessage: 'Allow this app to run inference.',
		description: 'Helper text next to the enabled toggle in the inference app modal.',
	},
	realtimeFieldLabel: {
		id: 'dtac.graphClawHub.realtimeFieldLabel.ai-non-final',
		defaultMessage: 'Realtime',
		description:
			'Label for the toggle that controls whether a new inference app uses realtime events.',
	},
	realtimeFieldDescription: {
		id: 'dtac.graphClawHub.realtimeFieldDescription.ai-non-final',
		defaultMessage: 'Run this app as source events arrive.',
		description: 'Helper text next to the realtime toggle in the inference app modal.',
	},
	enabledStatus: {
		id: 'dtac.graphClawHub.enabledStatus.ai-non-final',
		defaultMessage: 'Enabled',
		description: 'Status label shown on an inference app row when the app can run inference.',
	},
	disabledStatus: {
		id: 'dtac.graphClawHub.disabledStatus.ai-non-final',
		defaultMessage: 'Disabled',
		description: 'Status label shown on an inference app row when the app cannot run inference.',
	},
	enabledToggleLabel: {
		id: 'dtac.graphClawHub.enabledToggleLabel.ai-non-final',
		defaultMessage: 'Enable or disable {appName}',
		description:
			'Accessible label for the row toggle. The placeholder is replaced with the inference app name.',
	},
	enabledLozenge: {
		id: 'dtac.graphClawHub.enabledLozenge.ai-non-final',
		defaultMessage: 'Enabled',
		description: 'Status label shown on an inference app row when the app can run inference.',
	},
	disabledLozenge: {
		id: 'dtac.graphClawHub.disabledLozenge.ai-non-final',
		defaultMessage: 'Disabled',
		description: 'Status label shown on an inference app row when the app cannot run inference.',
	},
	realtimeLozenge: {
		id: 'dtac.graphClawHub.realtimeLozenge.ai-non-final',
		defaultMessage: 'Realtime',
		description:
			'Status label shown on an inference app row when the app runs as source events arrive.',
	},
	offlineLozenge: {
		id: 'dtac.graphClawHub.offlineLozenge.ai-non-final',
		defaultMessage: 'Offline',
		description:
			'Status label shown on an inference app row when the app does not run in realtime.',
	},
	enabledToggleAriaLabel: {
		id: 'dtac.graphClawHub.enabledToggleAriaLabel.ai-non-final',
		defaultMessage: 'Enable or disable {name}',
		description:
			'Accessible label for the row toggle. The placeholder is replaced with the inference app name.',
	},
	deleteButtonAriaLabel: {
		id: 'dtac.graphClawHub.deleteButtonAriaLabel.ai-non-final',
		defaultMessage: 'Delete {name}',
		description:
			'Accessible label for the row delete button. The placeholder is replaced with the inference app name.',
	},
	deleteModalTitle: {
		id: 'dtac.graphClawHub.deleteModalTitle.ai-non-final',
		defaultMessage: 'Delete inference app?',
		description: 'Title of the confirmation modal shown before deleting an inference app.',
	},
	deleteModalBody: {
		id: 'dtac.graphClawHub.deleteModalBody.ai-non-final',
		defaultMessage: 'Deleting {name} removes it from this tenant.',
		description:
			'Body copy in the delete confirmation modal. The placeholder is replaced with the inference app name.',
	},
	deleteModalConfirm: {
		id: 'dtac.graphClawHub.deleteModalConfirm.ai-non-final',
		defaultMessage: 'Delete',
		description: 'Label for the destructive confirmation button that deletes an inference app.',
	},
	cancelButton: {
		id: 'dtac.graphClawHub.cancelButton',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button in the inference app modal.',
	},
	createButton: {
		id: 'dtac.graphClawHub.createButton',
		defaultMessage: 'Add app',
		description: 'Label for the submit button that creates an inference app.',
	},
	confirmCreateModalTitle: {
		id: 'dtac.graphClawHub.confirmCreateModalTitle',
		defaultMessage: 'Start inference?',
		description: 'Title of the confirmation modal shown before creating a live inference app.',
	},
	confirmCreateModalDescription: {
		id: 'dtac.graphClawHub.confirmCreateModalDescription',
		defaultMessage:
			'This will start running the real inference pipeline, are you sure you want to begin inferring data?',
		description: 'Warning shown before a live inference app is created and begins processing data.',
	},
	confirmCreateButton: {
		id: 'dtac.graphClawHub.confirmCreateButton',
		defaultMessage: 'Start inference',
		description:
			'Label for the confirmation button that creates a live inference app and starts processing data.',
	},
	saveButton: {
		id: 'dtac.graphClawHub.saveButton.ai-non-final',
		defaultMessage: 'Save',
		description: 'Label for the submit button that saves edits to an inference app.',
	},
	actionErrorIconLabel: {
		id: 'dtac.graphClawHub.actionErrorIconLabel.ai-non-final',
		defaultMessage: 'Error',
		description: 'Accessible label for the error icon shown in a mutation failure flag.',
	},
	createActionErrorTitle: {
		id: 'dtac.graphClawHub.createActionErrorTitle.ai-non-final',
		defaultMessage: 'We couldn’t add the inference app',
		description:
			'Title of the dismissible error flag shown when creating a new inference app fails.',
	},
	updateActionErrorTitle: {
		id: 'dtac.graphClawHub.updateActionErrorTitle.ai-non-final',
		defaultMessage: 'We couldn’t update the inference app',
		description:
			'Title of the dismissible error flag shown when saving or toggling an inference app fails.',
	},
	deleteActionErrorTitle: {
		id: 'dtac.graphClawHub.deleteActionErrorTitle.ai-non-final',
		defaultMessage: 'We couldn’t delete the inference app',
		description: 'Title of the dismissible error flag shown when deleting an inference app fails.',
	},
	actionErrorDescription: {
		id: 'dtac.graphClawHub.actionErrorDescription.ai-non-final',
		defaultMessage: 'Try again. Your inference apps are unchanged.',
		description:
			'Body copy for a dismissible error flag shown after an inference app mutation fails.',
	},
	duplicateActionErrorDescription: {
		id: 'dtac.graphClawHub.duplicateActionErrorDescription.ai-non-final',
		defaultMessage: 'An inference app with these choices already exists.',
		description: 'Error flag description shown when the backend rejects a duplicate inference app.',
	},
	permissionActionErrorDescription: {
		id: 'dtac.graphClawHub.permissionActionErrorDescription.ai-non-final',
		defaultMessage: 'You don’t have permission to make this change.',
		description: 'Error flag description shown when an inference app mutation is not authorized.',
	},
	unavailableActionErrorDescription: {
		id: 'dtac.graphClawHub.unavailableActionErrorDescription.ai-non-final',
		defaultMessage:
			'One of the selected capabilities is no longer available. Choose an available option and try again.',
		description: 'Error flag description shown when the backend rejects an unavailable capability.',
	},
	validationActionErrorDescription: {
		id: 'dtac.graphClawHub.validationActionErrorDescription.ai-non-final',
		defaultMessage: 'Review the selected values and try again.',
		description:
			'Error flag description shown when the backend rejects invalid inference app values.',
	},
	promptOverrideTooLargeActionErrorDescription: {
		id: 'dtac.graphClawHub.promptOverrideTooLargeActionErrorDescription.ai-non-final',
		defaultMessage:
			'The additional instructions are too large. Shorten them and try again. Unicode characters can use more than one byte.',
		description:
			'Error flag description shown when the backend rejects instructions that exceed its UTF 8 byte limit.',
	},
	requestConflictActionErrorDescription: {
		id: 'dtac.graphClawHub.requestConflictActionErrorDescription.ai-non-final',
		defaultMessage: 'This create attempt changed after it started. Review the form and try again.',
		description:
			'Error flag description shown when a create request ID is reused with different values.',
	},
	notFoundActionErrorDescription: {
		id: 'dtac.graphClawHub.notFoundActionErrorDescription.ai-non-final',
		defaultMessage: 'This inference app no longer exists. Reload the list and try again.',
		description:
			'Error flag description shown when an inference app was removed before a mutation.',
	},
	dependencyActionErrorDescription: {
		id: 'dtac.graphClawHub.dependencyActionErrorDescription.ai-non-final',
		defaultMessage:
			'GraphClaw is temporarily unavailable. Try again. Your inference apps are unchanged.',
		description:
			'Error flag description shown when a GraphClaw backend dependency prevents a mutation.',
	},
	dismissActionError: {
		id: 'dtac.graphClawHub.dismissActionError.ai-non-final',
		defaultMessage: 'Dismiss',
		description: 'Label for the button that dismisses an inference app mutation failure flag.',
	},
	emptyStateHeader: {
		id: 'dtac.graphClawHub.emptyStateHeader',
		defaultMessage: 'No inference apps yet',
		description: 'Heading of the empty state shown when there are no inference apps.',
	},
	emptyStateDescription: {
		id: 'dtac.graphClawHub.emptyStateDescription',
		defaultMessage: 'Create your first inference app to start inferring insights from your tools.',
		description: 'Body of the empty state shown when there are no inference apps.',
	},
	loadingLabel: {
		id: 'dtac.graphClawHub.loadingLabel',
		defaultMessage: 'Loading inference apps',
		description: 'Accessible label for the loading state of the inference apps list.',
	},
	errorHeader: {
		id: 'dtac.graphClawHub.errorHeader',
		defaultMessage: 'Something went wrong',
		description: 'Heading of the error state shown when the inference apps fail to load.',
	},
	errorDescription: {
		id: 'dtac.graphClawHub.errorDescription',
		defaultMessage: 'We couldn’t load your inference apps. Please try again.',
		description: 'Body of the error state shown when the inference apps fail to load.',
	},
	permissionErrorHeader: {
		id: 'dtac.graphClawHub.permissionErrorHeader.ai-non-final',
		defaultMessage: 'Access needed',
		description: 'Heading shown when the current user cannot load GraphClaw inference apps.',
	},
	permissionErrorDescription: {
		id: 'dtac.graphClawHub.permissionErrorDescription.ai-non-final',
		defaultMessage: 'You don’t have permission to view inference apps for this site.',
		description: 'Description shown when the current user cannot load GraphClaw inference apps.',
	},
	retryButton: {
		id: 'dtac.graphClawHub.retryButton.ai-non-final',
		defaultMessage: 'Try again',
		description: 'Button label that retries loading the GraphClaw catalog and inference apps.',
	},
	catalogEmptyTitle: {
		id: 'dtac.graphClawHub.catalogEmptyTitle.ai-non-final',
		defaultMessage: 'No capabilities are available',
		description: 'Warning title shown when the backend GraphClaw catalog has no usable choices.',
	},
	catalogEmptyDescription: {
		id: 'dtac.graphClawHub.catalogEmptyDescription.ai-non-final',
		defaultMessage:
			'Creating an inference app is unavailable until the backend catalog has a source, target, and output.',
		description: 'Warning description shown when the backend GraphClaw catalog is incomplete.',
	},
	unavailableAppTitle: {
		id: 'dtac.graphClawHub.unavailableAppTitle.ai-non-final',
		defaultMessage: 'This app uses an unavailable capability',
		description: 'Warning title shown while editing a stored app with an unavailable capability.',
	},
	unavailableAppDescription: {
		id: 'dtac.graphClawHub.unavailableAppDescription.ai-non-final',
		defaultMessage: 'Its stored choices are preserved. Select available choices before saving.',
		description: 'Warning description shown while editing a stored app with unavailable choices.',
	},
	unavailableLozenge: {
		id: 'dtac.graphClawHub.unavailableLozenge.ai-non-final',
		defaultMessage: 'Unavailable',
		description: 'Status lozenge shown on a stored inference app with unavailable capabilities.',
	},
	matcherSummary: {
		id: 'dtac.graphClawHub.matcherSummary.ai-non-final',
		defaultMessage: 'When: {matchers}',
		description: 'Row summary of selected backend matcher labels for an inference app.',
	},
});
